import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiArrowLeft, FiInfo, FiCheck } = FiIcons;

// ✅ PRIMARY — always fires, success determined by this
const PRIMARY_WEBHOOK = 'https://forms.samsnutrihub.com/wp-json/gf/v2/workflow/webhooks/1/0yrhjosiE9Qlc6CxFbAN4f4eH';

// Secondary webhooks — fire in parallel, failures don't block success
const SECONDARY_WEBHOOKS = [
  'https://automator.itskaivalya.com/webhook/body-analysis-form',
  'https://forms.samsnutrihub.com/wp-json/uap/v2/uap-13-15',
];

const BodyAnalysisForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    age: '',
    gender: '',
    height: '',
    weight: '',
    idealWeight: '',
    extraWeight: '',
    lessWeight: '',
    bmi: '',
    bodyFat: '',
    visceralFat: '',
    subcutaneousWhole: '',
    subcutaneousTrunk: '',
    subcutaneousArm: '',
    subcutaneousLegs: '',
    restingMetabolism: '',
    bodyAge: '',
    skeletalMuscleWhole: '',
    skeletalMuscleTrunk: '',
    skeletalMuscleArm: '',
    skeletalMuscleLegs: '',
  });

  const totalSteps = 4;

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const nextStep = () => { if (currentStep < totalSteps) setCurrentStep(currentStep + 1); };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.name || !formData.phone) {
      alert('Please fill in all required fields (Name and WhatsApp Number).');
      setIsSubmitting(false);
      return;
    }

    const payload = JSON.stringify(formData);
    console.log('📤 Submitting body analysis payload:', JSON.parse(payload));

    try {
      // Always fire PRIMARY webhook — outcome depends on this
      const primaryPromise = fetch(PRIMARY_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      });

      // Fire secondary webhooks in parallel — independent of primary
      SECONDARY_WEBHOOKS.forEach((url, i) => {
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        })
          .then((res) => console.log(`✅ Secondary ${i + 1} (${url}) — ${res.status}`))
          .catch((err) => console.warn(`⚠️ Secondary ${i + 1} (${url}) failed:`, err));
      });

      // Await primary result
      const primaryResult = await primaryPromise;
      console.log(`✅ PRIMARY webhook — Status: ${primaryResult.status}`);

      if (primaryResult.ok) {
        alert('Thank you! Your body analysis has been submitted. Our team will contact you within 24 hours.');
      } else {
        console.warn('Primary webhook responded with non-OK status:', primaryResult.status);
        alert('Thank you! Your submission was received. Our team will be in touch soon.');
      }
    } catch (error) {
      console.error('❌ PRIMARY webhook failed:', error);
      alert('Network error. Please check your connection and try again, or contact us directly at hello@samsnutrihub.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-[#0B1320] font-poppins mb-6">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Name (First) *</label>
                <input
                  type="text" name="name" value={formData.name} onChange={handleInputChange} required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">WhatsApp Number *</label>
                <input
                  type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Email</label>
                <input
                  type="email" name="email" value={formData.email} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Age</label>
                <input
                  type="number" name="age" value={formData.age} onChange={handleInputChange} min="1" max="120"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent"
                  placeholder="25"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Gender</label>
                <select name="gender" value={formData.gender} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent">
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-[#0B1320] font-poppins mb-6">Basic Body Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Height (in centimeters)</label>
                <input type="number" name="height" value={formData.height} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" placeholder="170" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Weight (in Kg)</label>
                <input type="number" name="weight" value={formData.weight} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" placeholder="70" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Ideal Weight (in Kg)</label>
                <input type="number" name="idealWeight" value={formData.idealWeight} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Extra Weight (in Kg)</label>
                <input type="number" name="extraWeight" value={formData.extraWeight} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Less Weight (in Kg)</label>
                <input type="number" name="lessWeight" value={formData.lessWeight} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B1320] mb-2">BMI (Body Mass Index)</label>
              <div className="mb-2 text-xs text-gray-500">Normal: Male: 23%, Female: 22% | High: Male: 28%, Female: 28% | Risk: Male: 30%, Female: 30%</div>
              <input type="number" name="bmi" value={formData.bmi} onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" placeholder="24" />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-[#0B1320] font-poppins mb-6">Fat Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Body Fat Percentage</label>
                <div className="mb-2 text-xs text-gray-500">Normal: M: 17%, F: 24% | High: M: 20-25%, F: 30-35% | Risk: M: 25%, F: 35%</div>
                <input type="number" name="bodyFat" value={formData.bodyFat} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Visceral Fat Percentage</label>
                <div className="mb-2 text-xs text-gray-500">Normal: M: 5%, F: 7% | High: M: 14%, F: 15% | Risk: M: 15%, F: 17%</div>
                <input type="number" name="visceralFat" value={formData.visceralFat} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
            </div>
            <h4 className="font-semibold text-[#0B1320] mt-4">Whole Body Subcutaneous Fat</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Whole Body Subcutaneous</label>
                <p className="text-xs text-gray-500 mb-1">M: 15%, F: 20%</p>
                <input type="number" name="subcutaneousWhole" value={formData.subcutaneousWhole} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Whole Body Trunk</label>
                <p className="text-xs text-gray-500 mb-1">M: 15%, F: 20%</p>
                <input type="number" name="subcutaneousTrunk" value={formData.subcutaneousTrunk} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Whole Body Arm</label>
                <p className="text-xs text-gray-500 mb-1">M: 15%, F: 25%</p>
                <input type="number" name="subcutaneousArm" value={formData.subcutaneousArm} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Whole Body Legs</label>
                <p className="text-xs text-gray-500 mb-1">M: 20%, F: 25%</p>
                <input type="number" name="subcutaneousLegs" value={formData.subcutaneousLegs} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-[#0B1320] font-poppins mb-6">Muscle & Metabolism</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Resting Metabolism</label>
                <p className="text-xs text-gray-500 mb-1">Normal: M: 1400-1600, F: 1100-1300</p>
                <input type="number" name="restingMetabolism" value={formData.restingMetabolism} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Body Age</label>
                <p className="text-xs text-gray-500 mb-1">Age of your Cells and Tissues</p>
                <input type="number" name="bodyAge" value={formData.bodyAge} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
            </div>
            <h4 className="font-semibold text-[#0B1320] mt-4">Skeletal Muscle Analysis</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Muscle (Skeletal Muscle)</label>
                <p className="text-xs text-gray-500 mb-1">M: 37%, F: 33%</p>
                <input type="number" name="skeletalMuscleWhole" value={formData.skeletalMuscleWhole} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Skeletal Muscle Trunk</label>
                <p className="text-xs text-gray-500 mb-1">M: 30%, F: 25%</p>
                <input type="number" name="skeletalMuscleTrunk" value={formData.skeletalMuscleTrunk} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Skeletal Muscle Arm</label>
                <p className="text-xs text-gray-500 mb-1">M: 45%, F: 40%</p>
                <input type="number" name="skeletalMuscleArm" value={formData.skeletalMuscleArm} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B1320] mb-2">Skeletal Muscle Legs</label>
                <p className="text-xs text-gray-500 mb-1">M: 50%, F: 45%</p>
                <input type="number" name="skeletalMuscleLegs" value={formData.skeletalMuscleLegs} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] focus:border-transparent" />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="pt-16 bg-gradient-to-br from-[#F9FFF5] to-[#EAF8EC] min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">

        {/* Header */}
        <motion.div {...fadeInUp} className="text-center mb-12">
          <div className="inline-block mb-4">
            <span className="bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-medium">
              Free Assessment
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0B1320] font-poppins mb-4">
            Comprehensive Body Analysis
          </h1>
          <p className="text-xl text-[#4A4A4A]">
            Enter your measurement details to get a personalized analysis
          </p>
        </motion.div>

        {/* Progress Steps */}
        <motion.div {...fadeInUp} className="mb-12">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step <= currentStep ? 'bg-[#2E7D32] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step < currentStep ? <SafeIcon icon={FiCheck} className="h-5 w-5" /> : step}
                </div>
                {step < 4 && (
                  <div className={`w-16 md:w-24 h-1 mx-2 ${step < currentStep ? 'bg-[#2E7D32]' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm text-[#4A4A4A]">
            <span>Personal</span>
            <span>Basic Metrics</span>
            <span>Fat Analysis</span>
            <span>Muscle & Metab</span>
          </div>
        </motion.div>

        {/* Form Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <form onSubmit={currentStep === totalSteps ? handleSubmit : (e) => e.preventDefault()}>
            {renderStep()}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
              <button
                type="button" onClick={prevStep}
                disabled={currentStep === 1 || isSubmitting}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                  currentStep === 1 || isSubmitting
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-[#0B1320] hover:bg-gray-300'
                }`}
              >
                <SafeIcon icon={FiArrowLeft} className="h-4 w-4" />
                <span>Back</span>
              </button>

              {currentStep === totalSteps ? (
                <button
                  type="submit" disabled={isSubmitting}
                  className={`flex items-center space-x-2 bg-[#2E7D32] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#205C25] transition-colors duration-200 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  <span>{isSubmitting ? 'Submitting...' : 'Submit Analysis'}</span>
                  {!isSubmitting && <SafeIcon icon={FiCheck} className="h-4 w-4" />}
                </button>
              ) : (
                <button
                  type="button" onClick={nextStep}
                  className="flex items-center space-x-2 bg-[#2E7D32] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#205C25] transition-colors duration-200"
                >
                  <span>Next</span>
                  <SafeIcon icon={FiArrowRight} className="h-4 w-4" />
                </button>
              )}
            </div>
          </form>
        </motion.div>

        {/* Info Box */}
        <motion.div {...fadeInUp} className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiInfo} className="h-6 w-6 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">What Happens Next?</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Our expert team will analyze your profile within 24 hours</li>
                <li>• You'll receive a personalized nutrition assessment report</li>
                <li>• Free 15-minute consultation to discuss your results</li>
                <li>• Customized protocol recommendations based on your goals</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BodyAnalysisForm;